/**
 * تولیدکننده وعده‌های اضافی (بعد از صبحانه، بعد از ناهار)
 * این فایل توابع کمکی برای تولید وعده‌های اضافی را فراهم می‌کند
 * در واقع از generateMealMenu استفاده می‌کند اما با منطق خاص برای وعده‌های اضافی
 */

import { generateMealMenu } from './menuGenerator';
import { customRound, customRound2, customRoundFull, categorizeFraction } from './roundingUtils';

/**
 * تولید وعده بعد از صبحانه
 * این تابع از generateMealMenu استفاده می‌کند اما با منطق خاص برای وعده‌های اضافی
 * @param {number} afterbreakfastandlunchcal - کالری وعده بعد از صبحانه
 * @param {Array} formattedData - داده‌های فرمت شده منوها
 * @param {Object} selectedMenusHistory - تاریخچه منوهای انتخاب شده
 * @param {string} currentDate - تاریخ فعلی
 * @param {Function} onHistoryUpdate - تابع callback برای به‌روزرسانی تاریخچه
 * @param {Object} todaySelectedMenusRef - ref برای منوهای انتخاب شده امروز
 * @param {Array} previousDaysMenus - منوهای روزهای قبلی
 * @param {Array} usedMenus - لیست منوهای استفاده شده (برای جلوگیری از تکرار)
 * @returns {Object|null} - منوی تولید شده یا null
 */
export const generateAfterBreakfastMeal = (
  afterbreakfastandlunchcal,
  formattedData,
  selectedMenusHistory,
  currentDate,
  onHistoryUpdate = null,
  todaySelectedMenusRef = { menus: [] },
  previousDaysMenus = [],
  usedMenus = []
) => {
  try {
    if (!afterbreakfastandlunchcal || afterbreakfastandlunchcal <= 0) {
      console.warn("مقدار afterbreakfastandlunchcal نامعتبر است:", afterbreakfastandlunchcal);
      return null;
    }

    if (!formattedData || formattedData.length === 0) {
      console.warn("داده‌های منوها خالی هستند.");
      return null;
    }

    // استفاده از generateMealMenu برای تولید منو
    // اما با فیلتر اضافی برای جلوگیری از تکرار
    const mealType = "بعد از صبحانه";
    
    // فیلتر منوهای بعد از صبحانه که قبلاً استفاده نشده‌اند
    let availableMenus = formattedData.filter(
      (menu) => menu.meal === mealType && 
                menu.items.length > 0 &&
                (menu.form_type === "menu" || menu.form_type === "food_menu") &&
                menu.selected_food !== null && 
                menu.selected_food !== undefined &&
                typeof menu.selected_food === "string" && 
                menu.selected_food.trim().length > 0 &&
                !usedMenus.includes(menu.group_number)
    );

    // اگر همه منوها استفاده شده‌اند، ریست کن
    if (availableMenus.length === 0) {
      console.warn("هیچ منوی جدیدی موجود نیست، ریست کردن usedMenus");
      usedMenus = [];
      availableMenus = formattedData.filter(
        (menu) => menu.meal === mealType && 
                  menu.items.length > 0 &&
                  (menu.form_type === "menu" || menu.form_type === "food_menu") &&
                  menu.selected_food !== null && 
                  menu.selected_food !== undefined &&
                  typeof menu.selected_food === "string" && 
                  menu.selected_food.trim().length > 0
      );
    }

    if (availableMenus.length === 0) {
      console.error("هیچ منوی بعد از صبحانه معتبری یافت نشد.");
      return null;
    }

    // استفاده از generateMealMenu
    const result = generateMealMenu(
      mealType,
      afterbreakfastandlunchcal,
      availableMenus, // استفاده از منوهای فیلتر شده
      selectedMenusHistory,
      currentDate,
      onHistoryUpdate,
      todaySelectedMenusRef,
      previousDaysMenus
    );

    // اضافه کردن group_number به usedMenus
    if (result && result.menuId) {
      const selectedMenu = availableMenus.find(m => String(m.id) === String(result.menuId));
      if (selectedMenu && selectedMenu.group_number) {
        usedMenus.push(selectedMenu.group_number);
      }
    }

    return result;
  } catch (error) {
    console.error("خطا در تولید وعده بعد از صبحانه:", error);
    return null;
  }
};

/**
 * تولید وعده بعد از ناهار
 * این تابع از generateMealMenu استفاده می‌کند اما با منطق خاص برای وعده‌های اضافی
 * @param {number} afterlunchanddinnerCal - کالری وعده بعد از ناهار
 * @param {Array} formattedData - داده‌های فرمت شده منوها
 * @param {Object} selectedMenusHistory - تاریخچه منوهای انتخاب شده
 * @param {string} currentDate - تاریخ فعلی
 * @param {Function} onHistoryUpdate - تابع callback برای به‌روزرسانی تاریخچه
 * @param {Object} todaySelectedMenusRef - ref برای منوهای انتخاب شده امروز
 * @param {Array} previousDaysMenus - منوهای روزهای قبلی
 * @param {Array} usedMenus - لیست منوهای استفاده شده (برای جلوگیری از تکرار)
 * @returns {Object|null} - منوی تولید شده یا null
 */
export const generateAfterLunchMeal = (
  afterlunchanddinnerCal,
  formattedData,
  selectedMenusHistory,
  currentDate,
  onHistoryUpdate = null,
  todaySelectedMenusRef = { menus: [] },
  previousDaysMenus = [],
  usedMenus = []
) => {
  try {
    if (!afterlunchanddinnerCal || afterlunchanddinnerCal <= 0) {
      console.warn("مقدار afterlunchanddinnerCal نامعتبر است:", afterlunchanddinnerCal);
      return null;
    }

    if (!formattedData || formattedData.length === 0) {
      console.warn("داده‌های منوها خالی هستند.");
      return null;
    }

    // استفاده از generateMealMenu برای تولید منو
    const mealType = "بعد از ناهار";
    
    // فیلتر منوهای بعد از ناهار که قبلاً استفاده نشده‌اند
    let availableMenus = formattedData.filter(
      (menu) => menu.meal === mealType && 
                menu.items.length > 0 &&
                (menu.form_type === "menu" || menu.form_type === "food_menu") &&
                menu.selected_food !== null && 
                menu.selected_food !== undefined &&
                typeof menu.selected_food === "string" && 
                menu.selected_food.trim().length > 0 &&
                !usedMenus.includes(menu.group_number)
    );

    // اگر همه منوها استفاده شده‌اند، ریست کن
    if (availableMenus.length === 0) {
      console.warn("هیچ منوی جدیدی موجود نیست، ریست کردن usedMenus");
      usedMenus = [];
      availableMenus = formattedData.filter(
        (menu) => menu.meal === mealType && 
                  menu.items.length > 0 &&
                  (menu.form_type === "menu" || menu.form_type === "food_menu") &&
                  menu.selected_food !== null && 
                  menu.selected_food !== undefined &&
                  typeof menu.selected_food === "string" && 
                  menu.selected_food.trim().length > 0
      );
    }

    if (availableMenus.length === 0) {
      console.error("هیچ منوی بعد از ناهار معتبری یافت نشد.");
      return null;
    }

    // استفاده از generateMealMenu
    const result = generateMealMenu(
      mealType,
      afterlunchanddinnerCal,
      availableMenus, // استفاده از منوهای فیلتر شده
      selectedMenusHistory,
      currentDate,
      onHistoryUpdate,
      todaySelectedMenusRef,
      previousDaysMenus
    );

    // اضافه کردن group_number به usedMenus
    if (result && result.menuId) {
      const selectedMenu = availableMenus.find(m => String(m.id) === String(result.menuId));
      if (selectedMenu && selectedMenu.group_number) {
        usedMenus.push(selectedMenu.group_number);
      }
    }

    return result;
  } catch (error) {
    console.error("خطا در تولید وعده بعد از ناهار:", error);
    return null;
  }
};

/**
 * بررسی اینکه آیا باید وعده اضافی تولید شود یا نه
 * برای رژیم افزایش وزن، وعده‌های اضافی تولید می‌شوند
 * @param {string} dietOrder - نوع رژیم (increase یا decrease)
 * @returns {boolean} - آیا باید وعده اضافی تولید شود
 */
export const shouldGenerateAdditionalMeal = (dietOrder) => {
  return dietOrder === 'increase';
};

